import { useEffect, useRef, useState, type ChangeEvent } from "react";

import {
  classifyRunReadFailure,
  usePreviewRunQuery,
} from "@/api/previewInsights";
import { useSendManualTurnMutation } from "@/api/previewManual";
import {
  parseLabExport,
  type LabImport,
  type LabImportLine,
} from "@/lib/labImport";
import { RUN_POLL_INTERVAL_MS } from "@/lib/previewInsights";
import { Button } from "@/ui/Button";
import { Dialog, DialogClose, DialogContent } from "@/ui/Dialog";

/** Load conversation: reopens an exported run, or replays its patient lines into a new session (Liv answers them afresh). */
export function LabImportConversation({
  projectId,
  blocker,
  start,
  onStarted,
  onStatus,
}: {
  projectId: string;
  /** Why a new conversation cannot start now, or null. */
  blocker: string | null;
  /** Starts the session "New conversation" would and hands over its ref. */
  start: (created: (previewRunRef: string) => void) => void;
  onStarted: (previewRunRef: string) => void;
  /** Progress and refusals, shown where the tester sees them with the menu closed. */
  onStatus: (message: string | null) => void;
}) {
  const [pending, setPending] = useState<LabImport | null>(null);
  const [sending, setSending] = useState<{
    previewRunRef: string;
    lines: LabImportLine[];
  } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // The import whose run was read successfully: its probe stops polling.
  const [opened, setOpened] = useState<LabImport | null>(null);
  const probe = usePreviewRunQuery(projectId, pending?.runRef ?? "", {
    enabled: pending !== null && opened !== pending,
    pollIntervalMs: RUN_POLL_INTERVAL_MS,
  });
  const send = useSendManualTurnMutation(
    projectId,
    sending?.previewRunRef ?? "",
  );
  if (probe.isSuccess && opened !== pending) {
    setOpened(pending);
  }
  const missing =
    probe.isError && classifyRunReadFailure(probe.error).kind === "not_found";

  // Once the run is open the Lab's own read of it owns the polling.
  useEffect(() => {
    if (pending === null) {
      return;
    }
    if (probe.isSuccess) {
      onStatus(null);
      onStarted(pending.runRef);
    } else if (probe.isError && !missing) {
      onStatus("The original conversation could not be read. Try again.");
    }
    // `onStatus` and `onStarted` are the caller's inline callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, probe.isSuccess, probe.isError, missing]);

  useEffect(() => {
    if (sending === null) {
      return;
    }
    const { previewRunRef, lines } = sending;
    void (async () => {
      let failedAt: number | null = null;
      for (const [index, line] of lines.entries()) {
        onStatus(`Sending line ${index + 1} of ${lines.length}…`);
        try {
          await send.mutateAsync({
            text: line.text,
            expectedTurn: index + 1,
            idempotencyKey: crypto.randomUUID(),
          });
        } catch {
          failedAt = index + 1;
          break;
        }
      }
      onStatus(
        failedAt === null
          ? null
          : `Line ${failedAt} of ${lines.length} could not be sent. The conversation is open: continue it by hand.`,
      );
      setSending(null);
      onStarted(previewRunRef);
    })();
    // Runs once per import. `send` is bound to this render's run and changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sending]);

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file === undefined) {
      return;
    }
    const parsed = parseLabExport(await file.text(), projectId);
    onStatus(parsed.ok ? null : parsed.message);
    if (parsed.ok) {
      setPending(parsed.value);
      // The same run again: the query key is unchanged, so it will not re-read by itself.
      if (parsed.value.runRef === pending?.runRef) {
        void probe.refetch();
      }
    }
  }

  const count = pending?.lines.length ?? 0;
  const left = pending?.skipped ?? 0;
  const skippedNote =
    left > 0 ? ` Turns without patient text in the file are left out: ${left}.` : "";
  const stop =
    blocker ?? (count === 0 ? "The file has no patient lines to send." : null);

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        disabled={sending !== null}
        onClick={() => fileInput.current?.click()}
      >
        Load conversation
      </Button>
      <input
        ref={fileInput}
        type="file"
        accept=".json,application/json"
        hidden
        aria-label="Conversation file"
        onChange={(event) => void onFile(event)}
      />
      <p className="text-xs text-muted-foreground">
        Opens a conversation exported from Reliv Labs.
      </p>
      <Dialog
        open={missing}
        onOpenChange={(open) => (open ? undefined : setPending(null))}
      >
        <DialogContent
          title="Start a new conversation?"
          description={`The original conversation is no longer available. Start a new conversation with its ${count} patient ${count === 1 ? "line" : "lines"}? Liv answers them again with the testing version in the top bar.${skippedNote}`}
          footer={
            <>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                disabled={stop !== null}
                title={stop ?? undefined}
                onClick={() => {
                  if (pending === null) {
                    return;
                  }
                  const { lines } = pending;
                  setPending(null);
                  start((previewRunRef) => setSending({ previewRunRef, lines }));
                }}
              >
                Continue
              </Button>
            </>
          }
        />
      </Dialog>
    </div>
  );
}
